import {
  currencyChoices,
  DEFAULT_SETTINGS,
  findPreset,
  formatPricePerUnit,
  formatSpacing,
  LINE_COLORS,
  labelFor,
  type PencilMode,
  pricePerFtFromInput,
  type Units,
} from '@better-lights/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Download, FileDown, HardDrive } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAccount } from '../account/store.ts';
import { backupProjects, importProjects, pickFile } from '../app/projectActions.ts';
import { parsePrice } from '../lib/format.ts';
import { defaultUnits, lengthUnit, priceInputText } from '../lib/units.ts';
import { toast } from '../state/feedback.ts';
import { useLibrary } from '../state/library.ts';
import { useSettings } from '../state/settingsStore.ts';
import { BACKUP_EXTENSION } from '../storage/backup.ts';
import { requestPersistence, storage, storageEstimate } from '../storage/index.ts';
import { Button } from '../ui/Button.tsx';
import { Checkbox } from '../ui/Checkbox.tsx';
import { Field } from '../ui/Field.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import { Section } from '../ui/Section.tsx';
import { Segmented } from '../ui/Segmented.tsx';
import { SwatchFace } from '../ui/Swatch.tsx';

const mb = (b: number) => `${(b / 1024 / 1024).toFixed(b > 100 * 1024 * 1024 ? 0 : 1)} MB`;

function Card({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-3 rounded-md bg-surface p-4">{children}</div>;
}

function PriceList() {
  const s = useSettings();
  const keys = [...new Set([...LINE_COLORS.map((c) => c.hex as string), ...Object.keys(s.legendLabels)])].filter(
    (hex) => s.legendLabels[hex],
  );
  return (
    <div className="flex flex-col gap-3">
      {keys.map((hex) => {
        const l = labelFor(s.legendLabels, hex);
        return (
          <div key={hex} className="grid grid-cols-[6px_1fr] gap-3 rounded-md bg-surface2 p-3">
            <span className="rounded-full" style={{ background: hex }} />
            <div className="grid grid-cols-1 gap-2 min-[600px]:grid-cols-[1fr_1fr_120px]">
              <Field label="Name" value={l.name} onChange={(e) => s.setLegendLabel(hex, { name: e.target.value })} />
              <Field
                label="Bulb / product"
                placeholder="e.g. RGBW pairs, 12 in"
                value={l.product}
                onChange={(e) => s.setLegendLabel(hex, { product: e.target.value })}
              />
              <Field
                key={`${hex}-${l.pricePerFt}-${s.units}`}
                label="Price"
                prefix={s.currency}
                suffix={`/${lengthUnit(s.units)}`}
                inputMode="decimal"
                placeholder="none"
                defaultValue={priceInputText(l.pricePerFt, s.units)}
                onBlur={(e) => {
                  const v = parsePrice(e.target.value);
                  s.setLegendLabel(hex, { pricePerFt: v == null ? null : pricePerFtFromInput(v, s.units) });
                }}
              />
            </div>
          </div>
        );
      })}
      <span className="t-caption text-dim">
        New line colors appear here once you name or price them in a project with “Remember names & prices” on.
      </span>
    </div>
  );
}

export function SettingsScreen() {
  const s = useSettings();
  const navigate = useNavigate();
  const projects = useLibrary((l) => l.projects);
  const [estimate, setEstimate] = useState<Awaited<ReturnType<typeof storageEstimate>>>(null);
  const session = useAccount((a) => a.session);
  const look = findPreset(s.defaultColorId, s.customHex);

  useEffect(() => {
    void storageEstimate().then(setEstimate);
  }, []);

  return (
    <main
      className="min-h-dvh"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
    >
      <div className="flex min-h-11 items-center gap-2 px-2">
        <IconButton
          icon={ArrowLeft}
          label="Back"
          onClick={() => (history.length > 1 ? history.back() : void navigate({ to: '/' }))}
        />
        <h1 className="t-title">Settings</h1>
      </div>
      <div className="mx-auto flex max-w-[760px] flex-col gap-6 p-4">
        <Section title="Account">
          <Card>
            <span className="t-heading">{session ? (session.email ?? 'Signed in') : 'Guest'}</span>
            <span className="t-caption text-dim">
              {session
                ? 'Projects sync across your devices. Manage your plan, credits and workspaces in Account.'
                : 'Your projects stay on this device. Sign in to sync across devices and create photoreal AI renders.'}
            </span>
            <Button
              size="sm"
              className="self-start"
              onClick={() => void navigate({ to: session ? '/account' : '/signin' })}
            >
              {session ? 'Account' : 'Sign in'}
            </Button>
          </Card>
        </Section>

        <Section title="Price list">
          <Card>
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              <label className="flex flex-col gap-1">
                <span className="t-caption text-muted">Currency</span>
                <select
                  className="min-h-10 rounded-sm border border-border bg-surface2 px-2 text-[15px] text-text focus:border-accent focus:outline-none"
                  value={s.currency}
                  onChange={(e) => s.patch({ currency: e.target.value })}
                >
                  {currencyChoices(s.currency).map((c) => (
                    <option key={c.symbol} value={c.symbol}>
                      {c.symbol} · {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-col gap-1">
                <span className="t-caption text-muted" aria-hidden>
                  Units
                </span>
                <Segmented<Units>
                  label="Units"
                  className="w-[200px]"
                  value={s.units}
                  onChange={(units) => s.patch({ units })}
                  options={[
                    { value: 'imperial', label: 'Feet, inches' },
                    { value: 'metric', label: 'Metres, cm' },
                  ]}
                />
              </div>
              <span className="t-caption pb-3 text-dim">e.g. {formatPricePerUnit(28.5, s.currency, s.units)}</span>
            </div>
            <span className="t-caption text-dim">
              Default for your region: {defaultUnits() === 'metric' ? 'metric' : 'imperial'}. Projects always store
              feet; only what you see and type changes.
            </span>
            <Checkbox
              checked={s.legendEnabled}
              onChange={(v) => s.patch({ legendEnabled: v })}
              label="Show legend"
              caption="In Lines view and on Lines exports"
            />
            <Checkbox
              checked={s.rememberLegend}
              onChange={(v) => s.patch({ rememberLegend: v })}
              label="Remember names & prices"
              caption={
                s.rememberLegend
                  ? 'Edits in a project update your price list for new projects'
                  : 'Edits stay with each project only'
              }
            />
            <PriceList />
          </Card>
        </Section>

        <Section title="New projects">
          <Card>
            <div className="flex items-center gap-3">
              <SwatchFace colors={look.colors} size={28} />
              <div className="flex flex-col">
                <span className="t-label">{look.name}</span>
                <span className="t-caption text-dim">
                  {s.defaultStyle.layout === 'pair' ? 'Pairs' : 'Singles'} ·{' '}
                  {formatSpacing(s.defaultStyle.spacingIn, s.units)} spacing · LED size{' '}
                  {Math.round(s.defaultStyle.size * 100)}%
                </span>
              </div>
            </div>
            <span className="t-caption text-dim">
              Change these in the editor under Lights › Advanced › Save look as my default.
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="self-start"
              onClick={() => {
                s.patch({
                  defaultStyle: DEFAULT_SETTINGS.defaultStyle,
                  defaultColorId: DEFAULT_SETTINGS.defaultColorId,
                });
                toast('Default look reset');
              }}
            >
              Reset default look
            </Button>
          </Card>
        </Section>

        <Section title="Drawing">
          <Card>
            <span className="t-label text-dim">Input</span>
            <Segmented<PencilMode>
              label="Input"
              value={s.pencilMode}
              onChange={(v) => s.patch({ pencilMode: v })}
              options={[
                { value: 'auto', label: 'Auto' },
                { value: 'pencil', label: 'Stylus only' },
                { value: 'any', label: 'Touch too' },
              ]}
            />
            <Checkbox
              checked={s.freehandSnap}
              onChange={(v) => s.patch({ freehandSnap: v })}
              label="Snap freehand lines to the roof edge"
              caption="Finds the roofline near your stroke on this device (photo edges, plus depth when available)."
            />
          </Card>
        </Section>

        <Section title="Scene depth">
          <Card>
            <Checkbox
              checked={s.depthEnabled}
              onChange={(v) => s.patch({ depthEnabled: v })}
              label="Estimate depth on this device"
              caption="Keeps light off the sky and trees. Downloads a 27–50 MB model once; photos never leave the device."
            />
            <Button
              size="sm"
              variant="ghost"
              className="self-start"
              onClick={async () => {
                const keys = await storage().keys.list('cache/depth/');
                await Promise.all(keys.map((k) => storage().keys.delete(k)));
                toast(`Cleared ${keys.length} depth map${keys.length === 1 ? '' : 's'}`);
              }}
            >
              Clear depth cache
            </Button>
          </Card>
        </Section>

        <Section title="Export">
          <Card>
            <span className="t-label text-dim">Image format</span>
            <Segmented
              label="Image format"
              value={s.exportFormat}
              onChange={(v) => s.patch({ exportFormat: v })}
              options={[
                { value: 'jpeg', label: 'JPEG' },
                { value: 'png', label: 'PNG' },
              ]}
            />
          </Card>
        </Section>

        <Section title="Storage & backup">
          <Card>
            <div className="flex items-center gap-3">
              <HardDrive size={20} className="text-dim" aria-hidden />
              <div className="flex flex-col">
                <span className="t-label">
                  {estimate ? `${mb(estimate.usage)} used` : 'Storage'} ·{' '}
                  {storage().blobs.kind === 'opfs' ? 'Origin private file system' : 'IndexedDB'}
                </span>
                <span className="t-caption text-dim">
                  {estimate?.persisted
                    ? 'The browser will keep your projects.'
                    : 'The browser may clear projects when space runs low. Install the app or allow persistent storage to keep them.'}
                </span>
              </div>
            </div>
            {!estimate?.persisted ? (
              <Button
                size="sm"
                className="self-start"
                onClick={async () => {
                  const ok = await requestPersistence();
                  toast(
                    ok ? 'Projects will be kept on this device' : 'The browser did not allow persistent storage',
                    ok ? 'success' : 'default',
                  );
                  setEstimate(await storageEstimate());
                }}
              >
                Keep projects on this device
              </Button>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                icon={Download}
                disabled={projects.length === 0}
                onClick={() => void backupProjects(projects, 'better-lights-backup')}
              >
                Back up all projects
              </Button>
              <Button
                size="sm"
                icon={FileDown}
                onClick={async () => {
                  const f = await pickFile(`${BACKUP_EXTENSION},.json,application/zip`);
                  if (f) await importProjects(f);
                }}
              >
                Import backup
              </Button>
            </div>
          </Card>
        </Section>
        <nav className="t-caption flex justify-center gap-4 text-muted" aria-label="Legal">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/delete-account">Delete account</Link>
        </nav>
        <p className="t-caption text-center text-muted">
          Better Lights for the web · {import.meta.env.MODE === 'production' ? 'v0.1.0' : 'development'}
        </p>
      </div>
    </main>
  );
}
